import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Get system preference function
  const getSystemTheme = () => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    }
    return 'light';
  };

  const [theme, setTheme] = useState(() => {
    // On first visit, check if user has a saved preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }
    
    // If no saved preference, use system preference
    return getSystemTheme();
  });

  useEffect(() => {
    // Apply theme to document root and html element
    const root = document.documentElement;
    const html = document.querySelector('html');
    
    // Remove old theme first to force CSS recalculation
    root.removeAttribute('data-theme');
    html?.removeAttribute('data-theme');
    
    // Force a reflow to ensure CSS variables are recalculated
    void root.offsetHeight;
    
    // Set new theme
    root.setAttribute('data-theme', theme);
    html?.setAttribute('data-theme', theme);
    
    // Save theme preference (will be saved when user toggles via toggleTheme)
    // This ensures system preference is used on first visit, but user preference is saved when they toggle
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      localStorage.setItem('theme', theme);
    }
    
    // Dispatch a custom event to notify components of theme change
    window.dispatchEvent(new CustomEvent('themechange', { detail: { theme } }));
  }, [theme]);

  // Listen to system theme changes (only if user hasn't set a preference)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      const savedTheme = localStorage.getItem('theme');
      // Only update if user hasn't explicitly set a preference
      if (savedTheme !== 'dark' && savedTheme !== 'light') {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    }
    // Fallback for older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleSystemThemeChange);
      return () => mediaQuery.removeListener(handleSystemThemeChange);
    }
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'dark' ? 'light' : 'dark';
      // Save user's explicit preference when they toggle
      localStorage.setItem('theme', newTheme);
      return newTheme;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

